/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { BehaviorSubject, Observable, Subject, of, repeat } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { autocomplete } from './autocomplete.operator'
import { v4 as uuid } from 'uuid'
import { BASE_PATH } from '@myprofile-ui/pidx-utils'

export interface AutocompleteResult {
  label: string
  addressKey: string
}

export interface AutocompleteAddress {
  houseNumber: string
  street1: string
  supplement: string
  city: string
  postalCodeRegion: string
  postalCode: string
}

@Injectable({
  providedIn: 'root',
})
export class AutocompleteService {
  #basePath = inject(BASE_PATH)
  #httpClient = inject(HttpClient)

  public addressCountry!: string
  public accessToken!: string

  private _loading$$ = new BehaviorSubject<boolean>(false)
  private _error$$ = new BehaviorSubject<boolean>(false)
  private _searchTerm$$ = new Subject<string>()

  public readonly loading$ = this._loading$$.asObservable()
  public readonly error$ = this._error$$.asObservable()
  public readonly results$ = this._searchTerm$$.pipe(
    autocomplete<string, AutocompleteResult[]>((searchTerm) => this.fetchAutocompleteResults(searchTerm)),
    map((response) => {
      this._error$$.next(false)
      if (response) {
        return response?.map((address: AutocompleteResult) => ({
          label: address.label,
          value: address.addressKey,
        }))
      } else {
        return response
      }
    }),
    catchError(() => {
      this._error$$.next(true)
      return of([])
    }),
    tap(() => {
      this._loading$$.next(false)
    }),
    repeat(),
  )

  search(searchTerm: string): void {
    this._error$$.next(false)
    this._loading$$.next(true)
    this._searchTerm$$.next(searchTerm)
  }

  public fetchAutocompleteAddressDataForAddressKey(addressKey: string): Observable<AutocompleteAddress> {
    return this.#httpClient.get<AutocompleteAddress>(`${this.#basePath}/address/verification/${addressKey}`, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        'X-TRACE-ID': `MYPROFILE-AUTOCOMPLETE-${uuid()}`,
      },
    })
  }

  private fetchAutocompleteResults(search: string): Observable<AutocompleteResult[]> {
    const payload = {
      countryCode: this.addressCountry,
      searchString: search,
    }
    return this.#httpClient.post<AutocompleteResult[]>(`${this.#basePath}/address/verification`, payload, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        'X-TRACE-ID': `MYPROFILE-AUTOCOMPLETE-${uuid()}`,
      },
    })
  }
}
